import React, { memo, useCallback, useMemo, useState } from "react";

import {
  MdOutlineSecurity,
  MdAttachMoney,
  MdMessage,
  MdKeyboardArrowRight,
  MdOutlineChevronRight,
  MdOutlineReplay,
} from "react-icons/md";
import { Tooltip } from "antd";

import QuickAccessCardComponent from "../../../components/QuickAccessCard";
import { useClient } from "../../../hooks/ClientProvider";
import useInsurance from "../../../hooks/useInsurance";
import usePremiumAnnuity from "../../../hooks/usePremiumAnnuity";
import useSMS from "../../../hooks/useSMS";
import api from "../../../services/api";
import { formatDate } from "../../../utils/formatDate";
import { notifyError } from "../../../utils/notifications";
import useCheckPermission from "../../../utils/useCheckPermission";
import ModalListAnnuities from "../../home/modals/ModalListAnnuity";
import * as Style from "../style";
import ModalAcessAnnuity from "./ModalAcessAnnuity";
import ModalAcessInsurances from "./ModalAcessInsurances";
import ModalViewInsurances from "./ModalAcessInsurances/ModalViewInsurances";
import ModalAcessSMS from "./ModalAcessSMS";
import DSButton from "../../../components/DSButton";
import { usePermissions } from "../../../Context/permissionContext";
import {
  INSURANCE_ADHESION_PUSH_LABEL,
  INSURANCE_ADHESION_SAC_CARD_LABEL,
  resolveInsuranceAdhesionActionVisibility,
} from "../../../helpers/getInsuranceAdhesionActionVisibility";
import { useSendInsuranceAdhesionPush } from "../../../hooks/useSendInsuranceAdhesionPush";

import designSystemColors from "../../../styles";

function QuickAcessCardsComponent() {
  const { client } = useClient();
  const { permissions } = usePermissions();
  const { sendInsuranceAdhesionPush, sendingInsurancePush } =
    useSendInsuranceAdhesionPush();
  const { showSacInsuranceAction, showPushInsuranceAction } = useMemo(
    () => resolveInsuranceAdhesionActionVisibility(permissions),
    [permissions]
  );
  const hasPermissionViewSMSPackage = useCheckPermission("view_sms_package");
  const {
    ref: refSMS,
    reload: reloadPacoteSMS,
    listAllSMS,
    listContractedSMS,
    openSMSModal,
    setOpenSMSModal,
    isLoading: isLoadingSMS,
  } = useSMS(client.client.id);

  const {
    ref,
    isLoadingAnnuity,
    premiumAnnuity,
    allBonnusAnnuity,
    reload: reloadPremiumAnnuity,
    openModalAnnuityInformation,
    setOpenModalAnnuityInformation,
    selectedAnnuityInformation,
    handleOpenModalAnnuityInformation,
  } = usePremiumAnnuity();

  const {
    hasSecurity,
    loadingInsurance,
    insurances,
    reload: reloadInsurances,
    reloadAllInsurances,
    reloadCanceledInsurances,
    canceledInsurances,
    loadingCanceledInsurances,
    hasErrorOnInsurancesRequest,
  } = useInsurance(client?.client?.id);

  const hasActive = hasSecurity && insurances?.length > 0;
  const hasCanceled = canceledInsurances?.length > 0;
  const canShowInsuranceList =
    !hasErrorOnInsurancesRequest && (hasActive || hasCanceled);

  const [openModal, setOpenModal] = useState(false);
  const [openModalInsurances, setOpenModalInsurances] = useState(false);
  const [modalViewInsurancesIsOpen, setModalViewInsurancesIsOpen] =
    useState(false);
  const [openModalAnnuity, setOpenModalAnnuity] = useState(false);
  const [openModalSms, setOpenModalSms] = useState(false);
  const [openModalSMSList, setOpenModalSMSList] = useState(false);
  const [listAnnuities, setListAnnuities] = useState([]);
  const hasPermissionViewContractedInsurances = useCheckPermission("view_contracted_insurances");
    const hasPermissionListViewCanceledInsurances = useCheckPermission("list_canceled_insurance");


  const handleInsuranceClick = () => {
    if (hasPermissionViewContractedInsurances || hasPermissionListViewCanceledInsurances) {
      setModalViewInsurancesIsOpen(true);
    } else {
      notifyError(
        "Você não tem permissão para visualizar seguros."
      );
    }
  };

  const handleDependents = useCallback(async () => {
    try {
      const rawDependents = await api.get("client_dependants", {
        client_id: client?.client?.id,
        filter: "null" || "ativos",
      });
      const dependentsFormatted = rawDependents.data.map((dependent) => {
        dependent.dataNascimento = formatDate(dependent.dataNascimento);
        return dependent;
      });
      if (dependentsFormatted.length === 0) {
        return [];
      }
      return dependentsFormatted.filter((item) => item.status !== "C");
    } catch (error) {
      return [];
    }
  }, [api, client?.client?.id]);

  const handleOpenModal = useCallback(async () => {
    setOpenModal(true);
    setListAnnuities(allBonnusAnnuity);
  });

  const handleOpenModalInsurances = useCallback(() => {
    if (!hasSecurity) return;
    setOpenModalInsurances(true);
  }, [hasSecurity]);

  const openNewInsuranceModal = () => {
    setOpenModalInsurances(true);
    setModalViewInsurancesIsOpen(false);
  };

  const handleModalViewInsurances = () => {
    setModalViewInsurancesIsOpen((state) => !state);
  };

  return (
    <Style.QuickAccessCard>
      <div>
        <QuickAccessCardComponent
          loading={loadingInsurance || loadingCanceledInsurances}
          icon={<MdOutlineSecurity />}
          title="Seguros"
          dataTestId="botao_contratar_novo_seguro"
          contentLeft={
            <>
              {!hasSecurity && (
                <p>
                  Função de Seguros Desativada
                </p>
              )}

              {hasSecurity && hasErrorOnInsurancesRequest && (
                <Style.InsuranceErrorWrapper>
                  <p>Falha ao listar seguros contratados</p>

                  <Tooltip title="Recarregar seguros">
                    <DSButton
                      isIconAfter
                      icon={<MdOutlineReplay />}
                      isCancelType
                      onClick={reloadAllInsurances}
                      dataTestId="reload"
                      style={{
                        backgroundColor: designSystemColors.textGeneralButtons,
                      }}
                    />
                  </Tooltip>
                </Style.InsuranceErrorWrapper>
              )}

              {canShowInsuranceList && (
                <div
                  onClick={handleInsuranceClick}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    cursor: "pointer",
                    width: "60%",
                  }}
                >
                  <Style.InsurancesWrapper>
                    <p>Ativos: </p>
                    <strong
                      style={{
                        fontWeight: 700,
                        color: designSystemColors.textDark,
                      }}
                    >
                      {insurances.length}
                    </strong>
                  </Style.InsurancesWrapper>

                  <Style.InsurancesWrapper>
                    <p>Cancelados: </p>
                    <strong
                      style={{
                        fontWeight: 700,
                        color: designSystemColors.textDark,
                      }}
                    >
                      {canceledInsurances.length}
                    </strong>
                  </Style.InsurancesWrapper>
                </div>
              )}

              {hasSecurity &&
                insurances.length === 0 &&
                canceledInsurances.length === 0 &&
                !hasErrorOnInsurancesRequest && <p>Nenhum seguro contratado</p>}
            </>
          }
          permissionButton="insurance_Contract"
          onClickButton={handleOpenModalInsurances}
          actions={
            <>
              {showPushInsuranceAction && (
                <DSButton
                  type="button"
                  onClick={sendInsuranceAdhesionPush}
                  buttonText={INSURANCE_ADHESION_PUSH_LABEL}
                  icon={<MdOutlineChevronRight />}
                  isIconAfter
                  isLoading={sendingInsurancePush}
                  isDisabled={sendingInsurancePush}
                  disabled={sendingInsurancePush}
                  dataTestId="botao_link_contratacao_seguro_app"
                  buttonType="secondary"
                />
              )}
              {showSacInsuranceAction && (
                <DSButton
                  type="button"
                  onClick={handleOpenModalInsurances}
                  buttonText={INSURANCE_ADHESION_SAC_CARD_LABEL}
                  icon={<MdOutlineChevronRight />}
                  isIconAfter
                  dataTestId="botao_contratar_novo_seguro"
                  buttonType="primary"
                />
              )}
            </>
          }
        />
      </div>

      <div ref={ref}>
        <QuickAccessCardComponent
          loading={isLoadingAnnuity}
          icon={<MdAttachMoney />}
          title="Anuidade"
          dataTestId="botao_contratar_nova_anuidade"
          contentLeft={
            <>
              {premiumAnnuity && premiumAnnuity.length > 0 ? (
                <>
                  <p>Contratados:</p>
                  <p>
                    <strong onClick={() => setOpenModalAnnuity(true)}>
                      {premiumAnnuity ? premiumAnnuity.length : 0} Serviço
                      {premiumAnnuity && premiumAnnuity.length > 1 ? "s" : ""}
                      <MdKeyboardArrowRight />
                    </strong>
                  </p>
                </>
              ) : (
                <p>Nenhuma anuidade contratada</p>
              )}
            </>
          }
          permissionButton="join_premium_annuity"
          onClickButton={handleOpenModal}
        />
      </div>

      {hasPermissionViewSMSPackage ? (
        <div ref={refSMS}>
          <QuickAccessCardComponent
            loading={isLoadingSMS}
            icon={<MdMessage />}
            title="Pacote de SMS"
            dataTestId="botao_contratar_novo_pacote_sms"
            contentLeft={
              <>
                {listContractedSMS && listContractedSMS.length > 0 ? (
                  <>
                    <p>Contratados:</p>
                    <p>
                      <strong onClick={() => setOpenModalSMSList(true)}>
                        {listContractedSMS ? listContractedSMS.length : 0}{" "}
                        Serviço
                        {listContractedSMS && listContractedSMS.length > 1
                          ? "s"
                          : ""}
                        <MdKeyboardArrowRight />
                      </strong>
                    </p>
                  </>
                ) : (
                  <p>Nenhum Pacote de SMS contratado</p>
                )}
              </>
            }
            permissionButton="contract_sms_package"
            onClickButton={() => setOpenModalSms(true)}
            reloadPacoteSMS={reloadPacoteSMS}
          />
        </div>
      ) : null}

      <ModalAcessInsurances
        open={openModalInsurances}
        closeModal={() => setOpenModalInsurances(false)}
        clientId={client?.client?.id}
        clientInsurances={insurances}
        client={client}
        setDependents={handleDependents}
        reloadClientInsurances={reloadInsurances}
        canceledInsurances={canceledInsurances}
        hasSecurity={hasSecurity}
      />
      <ModalViewInsurances
        open={modalViewInsurancesIsOpen}
        closeModal={handleModalViewInsurances}
        insurance={insurances}
        clientId={client?.client?.id}
        clientInsurances={insurances}
        reloadClientInsurances={reloadAllInsurances}
        client={client}
        setDependents={handleDependents}
        openNewInsuranceModal={openNewInsuranceModal}
        canceledInsurances={canceledInsurances}
        reloadCanceledInsurances={reloadCanceledInsurances}
        hasSecurity={hasSecurity}
      />
      <ModalAcessAnnuity
        openModal={openModalAnnuity}
        titleModal="Anuidade contratada"
        onClose={() => setOpenModalAnnuity(false)}
        contentData={premiumAnnuity}
        handleOpenModalAnnuityInformation={handleOpenModalAnnuityInformation}
        openModalAnnuityInformation={openModalAnnuityInformation}
        setOpenModalAnnuityInformation={setOpenModalAnnuityInformation}
        reloadPremiumAnnuity={reloadPremiumAnnuity}
        selectedAnnuityInformation={selectedAnnuityInformation}
      />

      {/* Contratar Novo Pacote SMS */}
      <ModalAcessSMS
        openModal={openModalSms}
        openDetailsModal={openSMSModal}
        setDetailsModalOpen={setOpenSMSModal}
        titleModal="Pacote de SMS"
        onClose={() => setOpenModalSms(false)}
        pacoteSMS={listAllSMS}
        reloadPacoteSMS={reloadPacoteSMS}
      />

      {/* Pacotes SMS Contratados */}
      <ModalAcessSMS
        openModal={openModalSMSList}
        openDetailsModal={openSMSModal}
        setDetailsModalOpen={setOpenSMSModal}
        titleModal="Pacote de SMS"
        onClose={() => setOpenModalSMSList(false)}
        pacoteSMS={listContractedSMS}
        reloadPacoteSMS={reloadPacoteSMS}
        shouldShowStatusColumn
      />

      {/* Modal de Anuidades */}
      <ModalListAnnuities
        open={openModal}
        closeModal={() => {
          setOpenModal((prev) => !prev);
        }}
        reloadPremiumAnnuity={reloadPremiumAnnuity}
        listAnnuities={listAnnuities}
      />
    </Style.QuickAccessCard>
  );
}

export default memo(QuickAcessCardsComponent);
